import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import ProfileActions from './profile-actions';
import { useParams } from 'react-router';
import { ProfilePageParam } from '@webapp/types/navigation/params';
import AllPage from './pages/all';
import MusicPage from './pages/music';
import TourPage from './pages/tour';
import MerchPage from './pages/merch';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';
import TracPage from '@services/tracpage/types/trac-page';
import { SectionConfig } from '@services/tracpage/types/trac-page-section-config';
import { Dispatch } from 'redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { getProfileLinkWithParams } from '@webapp/utils/navigation/profile';
import {
  GetTracPage,
  UpdateTracPageDataField,
  UpdateTracPageField,
} from '@webapp/store/tracpage/action-creators';
import { TracPageFeatureItem } from '@services/tracpage/types/trac-page-feature-item';
import { ItemsConfig } from '@services/tracpage/types/trac-page-items-config';

import { TracpageThemeProvider } from '@shared-tracpage/contexts/theme-provider';
import { EditContextProvider } from '@shared-tracpage/contexts/edit';

import { Page } from '@shared-tracpage/components/page';
import Container from '@components/tracpage/container';
import { GetTourEvents } from '@webapp/store/traclink/action-creators';
import { GetCatalog } from '@webapp/store/catalog/action-creators';
import { MerchProduct } from '@services/merch/types/api/merch-product';
import { TourEvent } from '@common/types/traclink/tour-event';
import {
  getSortEventsFn,
  getSortMerchFn,
} from '@shared-tracpage/utils/items-sort';
import { GetArtistAnalytics } from '@webapp/store/artist-analytics/action-creators';
import { AnalyticsQueryTypes } from '@services/analytics/types/analytics-query-enum';
import { uploadImage } from '@webapp/sdk/artists';

const UPDATE_ACTION_CONFIG = {
  noAutosave: true,
  updateDraft: true,
};

type StateProps = {
  pageData: TracPage;
  merch: MerchProduct[];
  events: TourEvent[];
};
type DispatchProps = {
  dispatch: {
    getTracPage: () => GetTracPage;
    goToMain: () => CallHistoryMethodAction;
    getEvents: (ownerId: string) => GetTourEvents;
    getMerch: (ownerId: string) => GetCatalog;
    updateFeaturedItem: (
      item: TracPageFeatureItem
    ) => UpdateTracPageField<TracPage, 'featuredItem'>;
    clearFeaturedItem: () => UpdateTracPageField<TracPage, 'featuredItem'>;
    updateSectionsConfig: (
      newSectionsConfig: SectionConfig
    ) => UpdateTracPageField<TracPage, 'sectionsConfig'>;
    updateItemsConfig: (
      newItemsConfig: ItemsConfig
    ) => UpdateTracPageField<TracPage, 'itemsConfig'>;
    getArtistAnalytics: (type: AnalyticsQueryTypes) => GetArtistAnalytics;
    updateBio: (text: string) => UpdateTracPageDataField<TracPage, 'bio'>;
    updateAvatar: (url: string) => UpdateTracPageField<TracPage, 'avatar'>;
  };
};
type Props = StateProps & DispatchProps;

const mapStateToProps = (state: AppState): StateProps => ({
  pageData: state.tracpage.artistPageDraft,
  merch: state.merch.artistMerch,
  events: [
    ...state.traclink.tourEventDrafts.new,
    ...state.traclink.tourEvents.map(
      (te) => state.traclink.tourEventDrafts.edit[te.id] ?? te
    ),
  ],
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  dispatch: {
    getTracPage: () => dispatch(new GetTracPage()),
    goToMain: () => dispatch(push(getProfileLinkWithParams())),
    getEvents: (ownerId: string) => dispatch(new GetTourEvents(ownerId)),
    getMerch: (ownerId: string) =>
      dispatch(new GetCatalog({ artistId: ownerId })),
    updateFeaturedItem: (item: TracPageFeatureItem) =>
      dispatch(
        new UpdateTracPageField(
          'tracpage',
          'featuredItem',
          item,
          UPDATE_ACTION_CONFIG
        )
      ),
    clearFeaturedItem: () =>
      dispatch(
        new UpdateTracPageField(
          'tracpage',
          'featuredItem',
          null,
          UPDATE_ACTION_CONFIG
        )
      ),
    updateSectionsConfig: (config) =>
      dispatch(
        new UpdateTracPageField(
          'tracpage',
          'sectionsConfig',
          config,
          UPDATE_ACTION_CONFIG
        )
      ),
    updateItemsConfig: (config) =>
      dispatch(
        new UpdateTracPageField(
          'tracpage',
          'itemsConfig',
          config,
          UPDATE_ACTION_CONFIG
        )
      ),
    updateBio: (text) =>
      dispatch(
        new UpdateTracPageDataField(
          'tracpage',
          'bio',
          text,
          UPDATE_ACTION_CONFIG
        )
      ),
    updateAvatar: (url) =>
      dispatch(
        new UpdateTracPageField('tracpage', 'avatar', url, UPDATE_ACTION_CONFIG)
      ),
    getArtistAnalytics: (type) =>
      dispatch(new GetArtistAnalytics(type, undefined, true)),
  },
});

const ProfilePage: FC<Props> = ({ pageData, merch, events, dispatch }) => {
  const { page } = useParams<{ page: ProfilePageParam }>();

  useEffect(() => {
    dispatch.getTracPage();
    if (pageData.ownerId) {
      dispatch.getMerch(pageData.ownerId);
      dispatch.getEvents(pageData.ownerId);
    }
    dispatch.getArtistAnalytics(AnalyticsQueryTypes.COUNTS);
  }, [pageData.ownerId, dispatch]);

  const renderPage = () => {
    switch (page) {
      case ProfilePageParam.MAIN:
        return <AllPage />;
      case ProfilePageParam.MUSIC:
        return <MusicPage />;
      case ProfilePageParam.MERCH:
        return <MerchPage />;
      case ProfilePageParam.TOUR:
        return <TourPage />;
    }
  };

  if (!Object.values(ProfilePageParam).includes(page)) {
    dispatch.goToMain();
  }

  const handleFeature = (id: string, type: TracPageFeatureItem['type']) => {
    if (id === pageData.featuredItem?.id) {
      dispatch.clearFeaturedItem();
      return;
    }
    dispatch.updateFeaturedItem({ id, type });
  };

  const handleBioChange = (text: string) => {
    const trimmedText = text.trim();
    if (trimmedText === pageData.data.bio) return;

    dispatch.updateBio(trimmedText);
  };

  const handleAvatarChange = async (imageFile: File) => {
    try {
      const uploadResponse = await uploadImage(imageFile, 'avatar');
      uploadResponse.upload.request.then(() => {
        dispatch.updateAvatar(uploadResponse.url);
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Wrapper>
      <ProfileActions />
      <TracpageThemeProvider variant={pageData.theme}>
        <EditContextProvider
          merch={merch.sort(getSortMerchFn(pageData))}
          events={events.sort(getSortEventsFn(pageData))}
          editHandlers={{
            onFeature: handleFeature,
            onHide: dispatch.updateItemsConfig,
            onSectionsReorder: dispatch.updateSectionsConfig,
            onItemsReorder: dispatch.updateItemsConfig,
            onBioBlur: handleBioChange,
            onAvatarChange: handleAvatarChange,
          }}
          draft={pageData}
          editMode
        >
          <Page avatar={pageData.avatar} theme={pageData.theme}>
            <Container pageData={pageData}>{renderPage()}</Container>
          </Page>
        </EditContextProvider>
      </TracpageThemeProvider>
    </Wrapper>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfilePage);

const Wrapper = styled.main`
  width: 100%;
  height: 100%;
  position: relative;
`;
